// src/rms/pages/Availability.js
// ============================================================================
// Availability — blackout windows
// ============================================================================
// Before this existed, availability was derived purely from reservations, so
// there was no way to say "off the road for servicing" or "closed for the
// season" without retiring the asset entirely.
//
// Every vertical needs it: servicing, seasonal closure, owner use, deep
// cleaning, certification renewal, door replacement.
// ============================================================================

import React, { useMemo, useState } from "react";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import { formatMinutes } from "../pricing/availability";
import {
  PageHeader, Stat, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState, formatDate,
} from "../components/ui";
import adEngine from "../ads/adEngine";
import pinService from "../data/pinService";
import PinModal from "../components/PinModal";
import { noonInputInDays } from "../utils/datetime";

/**
 * Exported so the timeline offers the same list. A second copy would drift the
 * first time a type is added, and the two screens write the same column.
 */
export const BLOCK_TYPES = [
  { value: "maintenance", label: "Maintenance / servicing", labelKey: "opt.blockType.maintenance" },
  { value: "cleaning", label: "Cleaning / turnaround", labelKey: "opt.blockType.cleaning" },
  { value: "seasonal", label: "Seasonal closure", labelKey: "opt.blockType.seasonal" },
  { value: "owner_use", label: "Owner use", labelKey: "opt.blockType.ownerUse" },
  { value: "transit", label: "In transit between locations", labelKey: "opt.blockType.transit" },
  { value: "hold", label: "Soft hold", labelKey: "opt.blockType.hold" },
  { value: "unavailable", label: "Other", labelKey: "opt.blockType.unavailable" },
];

function Availability() {
  const { operator, member, t, tLower, invalidate } = useRms();
  const { rows: blocks } = useCollection("availabilityBlocks", {}, { orderBy: "starts_at" });
  const { rows: assets } = useCollection("assets", {}, { orderBy: "asset_code" });
  const { rows: groups } = useCollection("assetGroups");

  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  /**
   * A block waiting for a PIN.
   *
   * Every role may block dates -- the counter has to be able to take a vehicle
   * off the road without finding a manager. The signature is not a permission
   * check, it is the answer to "who did this", which the row could not
   * otherwise carry: created_by holds the auth user, and on a shared counter
   * session that is the same person for every row on the screen.
   */
  const [pendingBlock, setPendingBlock] = useState(null);

  const assetsById = useMemo(() => indexById(assets), [assets]);
  const groupsById = useMemo(() => indexById(groups), [groups]);

  const now = Date.now();
  const active = blocks.filter((b) => new Date(b.starts_at) <= now && new Date(b.ends_at) > now);
  const upcoming = blocks.filter((b) => new Date(b.starts_at) > now);

  const save = async (draft) => {
    if (!draft.target) return setError("Choose what to block.");
    if (new Date(draft.ends_at) <= new Date(draft.starts_at)) {
      return setError("The end must be after the start.");
    }
    adEngine.recordAction();
    const [kind, id] = draft.target.split(":");
    const payload = {
      operator_id: operator.id,
      asset_id: kind === "asset" ? id : null,
      asset_group_id: kind === "group" ? id : null,
      starts_at: new Date(draft.starts_at).toISOString(),
      ends_at: new Date(draft.ends_at).toISOString(),
      block_type: draft.block_type,
      reason: draft.reason || null,
      blocks_customer_booking: true,
      blocks_staff_booking: draft.block_type !== "hold",
    };
    // An edit is an ordinary write. Only creating one is signed, matching
    // agreements, where only the two legs are.
    if (draft.id) {
      await db.update("availabilityBlocks", draft.id, payload);
      setError(null);
      setEditing(null);
      invalidate();
      return;
    }
    setPendingBlock(payload);
  };

  /** First word of a display name, or the local part of the address. */
  const firstNameOf = (who) =>
    (who?.display_name || "").trim().split(/\s+/)[0] || (who?.email || "").split("@")[0] || null;

  /**
   * Sign, then write, in that order and in one gesture.
   *
   * created_by_name is sent, and on Supabase it is immediately overwritten by
   * the trigger with the name the PIN resolved to. That is not redundancy: the
   * client cannot know whose PIN was typed, so what it sends here is only ever
   * right on the local backend, where there is one member and no server to ask.
   * The value that reaches a real database comes from the signature.
   */
  const signAndCreate = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "block.create",
      subjectTable: "rms_availability_blocks",
    });
    if (!signed.success) return { error: signed.error };

    const who = signed.member || member;
    const created = await db.create("availabilityBlocks", {
      ...pendingBlock,
      created_signature_id: signed.data,
      created_by_name: firstNameOf(who),
    });
    if (!created.success) return { error: created.error };

    setPendingBlock(null);
    setError(null);
    setEditing(null);
    invalidate();
    return {};
  };

  const remove = async (id) => {
    await db.remove("availabilityBlocks", id);
    invalidate();
  };

  const targetLabel = (b) =>
    b.asset_id
      ? assetsById[b.asset_id]?.name || t("common.unknown", "Unknown")
      : `${groupsById[b.asset_group_id]?.name || t("availability.group", "Group")} ${t("availability.allUnits", "(all units)")}`;

  // Turnaround is the automatic cousin of a blackout — worth surfacing here so
  // staff understand why a same-day rebooking was refused.
  const turnaround = operator.default_turnaround_minutes || 0;

  return (
    <>
      <PageHeader
        title={t("availability.availability", "Availability")}
        subtitle={t("availability.subtitle", "Block {items} out for servicing, closures or owner use.", {
          items: tLower("assetPlural", "assets"),
        })}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blank())}>
            {t("availability.blockDates", "Block dates")}
          </button>
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-stat-row">
        <Stat label={t("availability.blockedNow", "Blocked now")} value={active.length} />
        <Stat label={t("availability.upcomingBlocks", "Upcoming blocks")} value={upcoming.length} />
        <Stat label={t("availability.turnaroundBuffer", "Turnaround buffer")} value={formatMinutes(turnaround)} hint="between every rental" />
        <Stat label={t("assetPlural", "Assets")} value={assets.length} />
      </div>

      {turnaround > 0 ? (
        <Banner tone="info">
          {t("availability.everyRentalIsAutomatically", "Every rental is automatically padded by")} <strong>{formatMinutes(turnaround)}</strong> of
          turnaround at both ends, so a return and the next pickup can never collide. That is applied
          on top of anything you block here.
        </Banner>
      ) : null}

      {blocks.length === 0 ? (
        <EmptyState
          title={t("availability.nothingBlocked", "Nothing blocked")}
          message={t("availability.empty.body", "All {items} are bookable except where reservations already exist.", {
            items: tLower("assetPlural", "assets"),
          })}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blank())}>
              {t("availability.blockDates", "Block dates")}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("assetSingular", "Asset")}</th>
                <th>{t("availability.from", "From")}</th>
                <th>To</th>
                <th>{t("availability.type", "Type")}</th>
                <th>{t("accessLog.reason", "Reason")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th>{t("availability.createdBy", "Created by")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => {
                const isActive = new Date(b.starts_at) <= now && new Date(b.ends_at) > now;
                const isPast = new Date(b.ends_at) <= now;
                return (
                  <tr key={b.id}>
                    <td><strong>{targetLabel(b)}</strong></td>
                    <td style={{ fontSize: 12.5 }}>{formatDate(b.starts_at)}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDate(b.ends_at)}</td>
                    <td style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>{b.block_type.replace(/_/g, " ")}</td>
                    <td style={{ fontSize: 12.5 }}>{b.reason || "—"}</td>
                    <td>
                      <StatusPill
                        status={isActive ? "maintenance" : isPast ? "completed" : "confirmed"}
                        label={isActive ? "blocked now" : isPast ? "past" : "scheduled"}
                      />
                    </td>
                    {/* Blank on anything blocked before signatures existed, and
                        on the sample block seeded with a new business. An em
                        dash rather than a guess. */}
                    <td style={{ fontSize: 12.5 }}>{b.created_by_name || "—"}</td>
                    <td className="rms-num" style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toDraft(b))}>
                        {t("action.edit", "Edit")}
                      </button>{" "}
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => remove(b.id)}>
                        {t("campaignForm.remove", "Remove")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <BlockForm
          draft={editing}
          assets={assets}
          groups={groups}
          t={t}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      ) : null}

      {/* Sits on top of the form rather than replacing it: cancelling the PIN
          returns to the filled-in block, so nobody retypes dates because they
          reached for the wrong four digits. */}
      {pendingBlock ? (
        <PinModal
          title={t("availability.signBlock", "Who is blocking this out?")}
          explain={t("availability.signBlockHelp", "Your PIN goes on the record beside it, so the list can say who.")}
          onCancel={() => setPendingBlock(null)}
          onResolved={signAndCreate}
        />
      ) : null}
    </>
  );
}

function BlockForm({ draft: initial, assets, groups, t, onCancel, onSave }) {
  const [draft, setDraft] = useState(initial);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Modal
      title={draft.id ? t("availability.editBlock", "Edit blocked dates") : t("availability.blockDates", "Block dates")}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>{t("action.cancel", "Cancel")}</button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => onSave(draft)}>{t("action.save", "Save")}</button>
        </>
      }
    >
      <Field label={t("availability.whatAreYouBlocking", "What are you blocking?")} required>
        <Select
          value={draft.target}
          onChange={(v) => set("target", v)}
          placeholder="Choose…"
          options={[
            ...groups.map((g) => ({ value: `group:${g.id}`, label: `${g.name} — all units` })),
            ...assets.map((a) => ({ value: `asset:${a.id}`, label: `${a.asset_code} — ${a.name}` })),
          ]}
        />
      </Field>

      <div className="rms-field-grid">
        <Field label={t("availability.from", "From")} required>
          <input className="rms-input" type="datetime-local" value={draft.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
        </Field>
        <Field label="To" required>
          <input className="rms-input" type="datetime-local" value={draft.ends_at} onChange={(e) => set("ends_at", e.target.value)} />
        </Field>
      </div>

      <Field label={t("availability.type", "Type")}>
        <Select value={draft.block_type} onChange={(v) => set("block_type", v)} options={BLOCK_TYPES} placeholder={t("availability.maintenance", "Maintenance")} />
      </Field>

      <Field label={t("availability.reason", "Reason")} help={t("availability.shownOnTheCalendar", "Shown on the calendar so staff know why it's blocked.")}>
        <TextInput value={draft.reason} onChange={(v) => set("reason", v)} placeholder={t("availability.annualService", "Annual service")} />
      </Field>

      {draft.block_type === "hold" ? (
        <Banner tone="info">
          {t("availability.aSoftHoldBlocks", "A soft hold blocks customer bookings but still lets staff book over it at the counter.")}
        </Banner>
      ) : null}
    </Modal>
  );
}

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function blank() {
  return {
    id: null,
    target: "",
    // Noon, not `now`. See utils/datetime -- a default of 12:37 reads as a
    // decision somebody made, and staff leave decisions alone.
    starts_at: noonInputInDays(0),
    ends_at: noonInputInDays(2),
    block_type: "maintenance",
    reason: "",
  };
}

function toDraft(b) {
  return {
    id: b.id,
    target: b.asset_id ? `asset:${b.asset_id}` : `group:${b.asset_group_id}`,
    starts_at: toLocalInput(new Date(b.starts_at)),
    ends_at: toLocalInput(new Date(b.ends_at)),
    block_type: b.block_type,
    reason: b.reason || "",
  };
}

export default Availability;
